import { IDestroyable, safeDestroys } from './Destroyable';

export type IDelegate = (...items: any[]) => void;
type GenericDelegate = IMulticastDelegate<IDelegate>;

export interface IMulticastDelegate<T extends IDelegate> extends IDestroyable {
    enabled: boolean;
    readonly fire: T;
    readonly canFire: boolean;

    add(h: T): Subscriber;

    remove(h: T): void;

    clear(): void;

    toFunction(): T;
}

export class Subscriber implements IDestroyable {
    constructor(private readonly delegate: GenericDelegate, private readonly fn: IDelegate) {}

    public destroy(): void {
        this.delegate.remove(this.fn);
    }

    public toBag(bag: DestroyableBag<IDestroyable | JQuery<HTMLElement>>): void {
        bag.add(this);
    }
}

//TODO: remove T type argument
export class DestroyableBag<T extends IDestroyable | JQuery<HTMLElement>> {
    private readonly destroyableList: T[] = [];

    public add(...destroyable: T[]): void {
        this.destroyableList.push(...destroyable);
    }

    public destroy(): void {
        for (const destroyable of this.destroyableList) {
            try {
                safeDestroys(destroyable);
            } catch (e) {
                console.error(e);
            }
        }
        this.destroyableList.length = 0;
    }
}

export type ISelfSubscribe<T extends IDelegate> = IMulticastDelegate<T> & T & ((addHandler: T) => Subscriber);

class ExtensibleFunction extends Function {
    constructor(f: () => unknown) {
        super();
        return Object.setPrototypeOf(f, new.target.prototype);
    }
}

export class MulticastDelegate extends ExtensibleFunction implements IMulticastDelegate<IDelegate> {
    private readonly fns: IDelegate[] = [];
    private readonly memorize: boolean = false;
    private readonly once: boolean = false;

    private isEnabled = true;
    private lastArgs: any[] | null = null;

    constructor(base?: IDelegate);
    constructor(memorize?: boolean);
    constructor(param?: { memorize?: boolean; once?: boolean });
    constructor(param?: IDelegate | boolean | { memorize?: boolean; once?: boolean }) {
        super((...arg: any[]) => {
            if (arg.length === 1) {
                const fn = arg[0];
                if (_.isFunction(fn)) {
                    return this.add(fn);
                }
                if (MulticastDelegate.isMulticastDelegate(fn)) {
                    return this.add(fn.toFunction());
                }
            }
            return this.fire(...arg);
        });
        if (!param) {
            return;
        }
        if (_.isFunction(param)) {
            this.add(param);
        } else if (typeof param === 'boolean') {
            this.memorize = param;
        } else {
            this.memorize = param.memorize ?? false;
            this.once = param.once ?? false;
        }
    }

    public get enabled(): boolean {
        return this.isEnabled;
    }

    public set enabled(value: boolean) {
        this.isEnabled = value;
    }

    public get canFire(): boolean {
        return this.isEnabled && this.fns.length > 0;
    }

    public static isMulticastDelegate(obj: any): obj is MulticastDelegate {
        return obj && obj.constructor === MulticastDelegate;
    }

    public static create<T extends IDelegate>(
        { memorize, base, once }: CreateSettings<T> = {
            memorize: false,
            base: undefined,
            once: false
        }
    ): ISelfSubscribe<T> {
        const delegate = new MulticastDelegate({ memorize, once });
        base && delegate.add(base);
        return delegate as any;
    }

    public add(fn: IDelegate): Subscriber {
        if (this.once) {
            this.clear();
        }
        if (this.memorize && this.lastArgs) {
            fn(...this.lastArgs);
        }
        this.fns.push(fn);
        return new Subscriber(this, fn);
    }

    public remove(fn: IDelegate): void {
        _.remove(this.fns, f => f === fn);
    }

    public fire(...items: any[]): any {
        if (this.isEnabled) {
            if (this.memorize) {
                this.lastArgs = items;
            }
            const res = this.fns.map(fn => fn(...items));
            return _.last(res);
        }
    }

    public toFunction(): IDelegate {
        return (...items: any[]) => this.fire(...items);
    }

    public clear(): void {
        this.fns.length = 0;
    }

    public destroy(): void {
        this.clear();
    }
}

type CreateSettings<T> = {
    memorize?: boolean;
    base?: T;
    once?: boolean;
};

// TODO: remove use only DestroyableBag
export class SubscriberBag extends DestroyableBag<Subscriber> {}

export class MulticastDelegateBag extends DestroyableBag<GenericDelegate> {
    public create<T extends IDelegate>(
        { memorize, once }: { memorize?: boolean; once?: boolean } = {
            memorize: false,
            once: false
        }
    ): ISelfSubscribe<T> {
        const res = MulticastDelegate.create<T>({ memorize, once });
        this.add(res);
        return res;
    }
}
